import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { axios_tenant } from "../../../axios";
import { DeleteForever } from "@mui/icons-material";
import { toast } from "react-toastify";

const UploadDocModal = ({ open, onClose, requestId, loadRequests, document, documentKey }) => {
  const [documentUnavailable, setDocumentUnavailable] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const xmlDocsKey = new Set(["iasrStatus", "isa210XMLStatus", "tisrXMLStatus"]);
  const singleFileKeys = new Set(["iasrStatus", "tisrPDFStatus", "tisrXMLStatus", "isa210XMLStatus", "isa210Status"]);

  const handleCheckboxChange = (event) => {
    setDocumentUnavailable(event.target.checked);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    if (singleFileKeys.has(documentKey)) {
      setSelectedFiles(files.slice(0, 1));
    } else {
      setSelectedFiles((prev) => [...prev, ...files]);
    }

    event.target.value = null;
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };

  const submitRequest = async () => {
    setLoading(true);

    if (!documentUnavailable) {
      if (singleFileKeys.has(documentKey) && selectedFiles.length > 1) {
        toast.error("Only one file allowed for this document");
        setLoading(false);
        return;
      }

      let allowedExtensions = [];

      if (documentKey === "seqListStatus") {
        allowedExtensions = ["xml", "txt", "pdf"];
      } else if (xmlDocsKey.has(documentKey)) {
        allowedExtensions = ["xml"];
      } else {
        allowedExtensions = ["pdf"];
      }

      for (const file of selectedFiles) {
        const ext = file.name.split(".").pop()?.toLowerCase();

        if (!allowedExtensions.includes(ext)) {
          toast.error(`Allowed file types: ${allowedExtensions.join(", ").toUpperCase()}`);
          setLoading(false);
          return;
        }
      }
    }

    const formData = new FormData();
    formData.append("requestId", requestId);

    if (documentUnavailable) {
      formData.append("documentsNotAvailable", [documentKey]);
    }

    if (selectedFiles.length) {
      const fileKey = documentKey.toUpperCase().replace("STATUS", "");
      selectedFiles.forEach((file) => {
        formData.append(fileKey, file);
      });
    }

    try {
      const response = await axios_tenant.put("/reference-downloader/pctrequests", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        toast.success("Request updated successfully");
        await loadRequests();
        onClose();
      }
    } catch (error) {
      toast.error("Failed to update request");
    } finally {
      setLoading(false);
    }
  };

  const getAcceptedFileTypes = () => {
    if (documentKey === "seqListStatus") {
      return ".xml,.txt,.pdf";
    }

    if (xmlDocsKey.has(documentKey)) {
      return ".xml";
    }

    return ".pdf";
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle mt={2}>{document}</DialogTitle>
      <DialogContent>
        <Box my={4} width={500} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
          <Box>
            <Typography>Document</Typography>

            <Box>
              {/* Show input conditionally */}
              {(!singleFileKeys.has(documentKey) || selectedFiles.length === 0) && <input type="file" accept={getAcceptedFileTypes()} disabled={documentUnavailable} onChange={handleFileChange} multiple={!singleFileKeys.has(documentKey)} />}
            </Box>
          </Box>

          <Box display={"flex"} alignItems={"center"}>
            <Checkbox
              name="documentUnavailable"
              checked={documentUnavailable}
              onChange={handleCheckboxChange}
              disabled={selectedFiles.length > 0}
              sx={{
                "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
              }}
            />
            <Typography noWrap>Document Not Available</Typography>
          </Box>
        </Box>

        {/* Always show selected files */}
        {selectedFiles.map((file, index) => (
          <Box key={index} display="flex" alignItems="center" justifyContent={"space-between"} mb={1}>
            <Typography flexWrap={"wrap"}>{file.name}</Typography>
            <IconButton size="small" onClick={() => handleRemoveFile(index)} sx={{ ml: 1 }}>
              <DeleteForever fontSize="small" color="error" />
            </IconButton>
          </Box>
        ))}
        {documentUnavailable && documentKey === "iasrStatus" ? (
          <Box>
            <Typography>"Since IASR is a mandatory document, marking it as unavailable will also mark all associated documents as unavailable"</Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {loading ? (
          <CircularProgress />
        ) : (
          <Button variant="contained" color="primary" onClick={submitRequest} disabled={loading || (!selectedFiles.length && !documentUnavailable)}>
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocModal;
